import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { Column, Row } from "@/shared/ui/Blocks";
import { Icon } from "@/shared/ui/Icon";
import { cn } from "@/shared/utils/shadcn";
import { Touchable } from "@/shared/ui/Touchable";

export interface ExpandableRowProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "id"> {
  id?: number;
  title?: string;
  description?: string | ReactNode;
  children: React.ReactNode;
  isExpanded?: boolean;
  onExpand?: (id?: number) => void;
  TitleComponent?: ({ isExpanded }: { isExpanded: boolean }) => ReactNode;
  TitleLeftComponent?: ReactNode;
  TitleRightComponent?: ReactNode;
}

const ExpandableRow: React.FC<ExpandableRowProps> = (props) => {
  const {
    TitleLeftComponent,
    TitleRightComponent,
    TitleComponent,
    title,
    description,
    children,
    className,
    isExpanded,
    id,
    onExpand,
    ...rest
  } = props;

  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(
    undefined
  );

  // Update content height whenever children change or when expanded state changes
  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [children, isExpanded]);

  // Set up ResizeObserver to track content height changes
  useEffect(() => {
    if (contentRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newHeight = entry.target.scrollHeight;
          if (newHeight !== contentHeight) {
            setContentHeight(newHeight);
          }
        }
      });

      resizeObserver.observe(contentRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [contentHeight]);

  const handleClick = () => {
    const target = document.activeElement as HTMLElement;
    if (
      target?.closest(
        "input, button, [role=\"button\"], [contenteditable=\"true\"]"
      )
    ) {
      return;
    }
    onExpand && onExpand(id);
  };

  const CollapsedComponent = useCallback(
    ({ isExpanded }: { isExpanded: boolean }) => {
      if (TitleComponent) {
        return <TitleComponent isExpanded={!!isExpanded} />;
      }

      return (
        <Row className="flex-1 items-center">
          {TitleLeftComponent}
          <div className="flex flex-1 items-center">
            <p className={cn("text-gray-900", isExpanded && "font-bold")}>
              {title}
            </p>
            <Icon
              icon="ChevronDown"
              size="sm"
              color="blue"
              className={`transition-transform duration-300 ml-2 ${
                isExpanded ? "rotate-180" : ""
              }`}
            />
          </div>
          {TitleRightComponent}
        </Row>
      );
    },
    [TitleComponent, TitleLeftComponent, TitleRightComponent, title]
  );

  return (
    <div className="overflow-hidden">
      <Touchable
        className="shadow-none hover:shadow-none cursor-pointer"
        onClick={handleClick}
      >
        <Column
          className={cn(
            "w-full py-4 border-b border-solid border-grey-300",
            className
          )}
          {...rest}
        >
          <Row className="flex-1 items-center justify-between">
            <CollapsedComponent isExpanded={!!isExpanded} />
          </Row>
        </Column>
      </Touchable>
      <div
        ref={contentRef}
        style={{
          maxHeight: isExpanded ? `${contentHeight}px` : "0px",
          height: isExpanded ? `${contentHeight}px` : "0px",
          opacity: isExpanded ? 1 : 0,
          transition:
            "max-height 0.3s ease-in-out, height 0.3s ease-in-out, opacity 0.3s ease-in-out",
          overflow: "hidden",
        }}
      >
        {isExpanded && (
          <div className={cn(!children && "py-2")}>
            {children || <span>{description}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export { ExpandableRow };
